import { formatDurationMinutes } from './dateTime';
import { spreadMinutesFromDurations } from './workDuration';
import { resolveWorkHoursCoverage, type WorkHoursCoverage } from './workHoursCoverage';

/**
 * Policy / shift configured minutes (Expected Work Hours, Spread Duration).
 */
export function policyMinutes(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value;
}

/**
 * Spread Duration from Shift / Work Policy.
 * Prefer an explicit spread; otherwise expected + configured lunch break.
 * If the API omits policy lunch, fall back to the day's recorded break so
 * Total Work Hours can still be validated (green/red) instead of staying plain.
 */
export function resolveAttendanceSpreadMinutes(options: {
  spreadMinutes?: number | null;
  expectedWorkMinutes?: number | null;
  policyLunchBreakMinutes?: number | null;
  actualBreakMinutes?: number | null;
}): number | null {
  const spread = policyMinutes(options.spreadMinutes);
  if (spread != null) return spread;

  const expected = policyMinutes(options.expectedWorkMinutes);
  if (expected == null) return null;

  const lunch =
    typeof options.policyLunchBreakMinutes === 'number' &&
    Number.isFinite(options.policyLunchBreakMinutes) &&
    options.policyLunchBreakMinutes >= 0
      ? options.policyLunchBreakMinutes
      : typeof options.actualBreakMinutes === 'number' &&
          Number.isFinite(options.actualBreakMinutes) &&
          options.actualBreakMinutes >= 0
        ? options.actualBreakMinutes
        : null;
  if (lunch == null) return null;
  return spreadMinutesFromDurations(expected, lunch);
}

/**
 * Total Work Hours = actual worked minutes + break minutes for the day.
 * Prefer an explicit gross/total when provided; otherwise sum worked + break.
 */
export function actualTotalWorkMinutes(options: {
  workedMinutes?: number | null;
  breakMinutes?: number | null;
  grossWorkMinutes?: number | null;
}): number | null {
  const gross = options.grossWorkMinutes;
  if (typeof gross === 'number' && Number.isFinite(gross) && gross >= 0) {
    return gross;
  }
  const worked = options.workedMinutes;
  if (typeof worked !== 'number' || !Number.isFinite(worked) || worked < 0) {
    return null;
  }
  const breakMinutes =
    typeof options.breakMinutes === 'number' &&
    Number.isFinite(options.breakMinutes) &&
    options.breakMinutes > 0
      ? options.breakMinutes
      : 0;
  return worked + breakMinutes;
}

/**
 * Total Work Hours, Short Hours and Not Covered are final only after a valid
 * check-out. The API's `isCheckedOut` wins when present (it knows about an
 * open session); otherwise a recorded check-out time decides.
 */
export function isAttendanceCheckedOut(options: {
  isCheckedOut?: boolean | null;
  checkOutTime?: string | null;
}): boolean {
  if (typeof options.isCheckedOut === 'boolean') return options.isCheckedOut;
  return typeof options.checkOutTime === 'string' && options.checkOutTime.trim() !== '';
}

/**
 * Total Work Hours for a completed session only; null before check-out so a
 * break is never shown (or validated against Spread Duration) as work time.
 * Prefers the API's checkout-gated `totalWorkMinutes` when it sends one.
 */
export function checkedOutTotalWorkMinutes(options: {
  isCheckedOut?: boolean | null;
  checkOutTime?: string | null;
  totalWorkMinutes?: number | null;
  workedMinutes?: number | null;
  breakMinutes?: number | null;
  grossWorkMinutes?: number | null;
}): number | null {
  if (!isAttendanceCheckedOut(options)) return null;
  const reported = options.totalWorkMinutes;
  if (typeof reported === 'number' && Number.isFinite(reported) && reported >= 0) {
    return reported;
  }
  return actualTotalWorkMinutes(options);
}

/**
 * True when actual Total Work Hours covers configured Spread Duration.
 * Spread comes from the assigned Shift / Work Policy — never hardcoded.
 */
export function resolveTotalHoursCoverage(
  totalWorkMinutes: number | null | undefined,
  spreadMinutes: number | null | undefined,
): WorkHoursCoverage {
  return resolveWorkHoursCoverage(
    typeof totalWorkMinutes === 'number' && Number.isFinite(totalWorkMinutes)
      ? totalWorkMinutes
      : null,
    policyMinutes(spreadMinutes),
  );
}

export function formatPolicyHours(
  value: number | null | undefined,
  empty = '—',
): string {
  const minutes = policyMinutes(value);
  if (minutes == null) {
    if (typeof value === 'number' && Number.isFinite(value) && value === 0) {
      return formatDurationMinutes(0);
    }
    return empty;
  }
  return formatDurationMinutes(minutes);
}

export function formatTotalWorkHours(
  value: number | null | undefined,
  empty = '—',
): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return empty;
  }
  return formatDurationMinutes(value);
}

export function totalHoursColor(
  coverage: WorkHoursCoverage,
  palette: {
    error: string;
    success?: string;
    textSecondary: string;
    textPrimary?: string;
  },
  fallback: string,
): string {
  if (coverage === 'not_covered') return palette.error;
  if (coverage === 'covered' && palette.success) return palette.success;
  return fallback;
}
